import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Plus, Clock, CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameMonth, isSameDay } from 'date-fns';
import { cn } from '@/lib/utils';

interface StaffSchedule {
  user_id: string;
  role: 'MANAGER' | 'SERVICE_WRITER';
  shift_start: string;
  shift_end: string;
  working_days: number[];
  time_off?: string[];
  recurring_breaks?: Array<{
    name: string;
    start_time: string;
    end_time: string;
  }>;
  non_recurring_breaks?: Array<{
    name: string;
    date: string;
    start_time: string;
    end_time: string;
  }>;
  display_name?: string;
  /** Core-100 direction 12: a technician who also works as a Service Advisor, added explicitly. */
  multi_role?: boolean;
}

interface StaffScheduleEditorProps {
  schedule: StaffSchedule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (schedule: StaffSchedule) => void;
}

const daysOfWeek = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

export const StaffScheduleEditor = ({ schedule, open, onOpenChange, onSave }: StaffScheduleEditorProps) => {
  const [editedSchedule, setEditedSchedule] = useState<StaffSchedule | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (schedule) {
      setEditedSchedule({ ...schedule });
    }
  }, [schedule]);

  if (!editedSchedule) return null;

  const toggleWorkingDay = (day: number) => {
    const working_days = editedSchedule.working_days.includes(day)
      ? editedSchedule.working_days.filter(d => d !== day)
      : [...editedSchedule.working_days, day].sort((a, b) => a - b);
    setEditedSchedule({ ...editedSchedule, working_days });
  };

  const handleDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeOff = editedSchedule.time_off || [];
    const isTimeOff = timeOff.includes(dateStr);
    
    let newTimeOff: string[];
    if (isTimeOff) {
      // Remove from time off
      newTimeOff = timeOff.filter(d => d !== dateStr);
    } else {
      // Add to time off
      newTimeOff = [...timeOff, dateStr];
    }
    
    setEditedSchedule({
      ...editedSchedule,
      time_off: newTimeOff
    });
  };

  const isScheduledDay = (date: Date) => {
    const dayOfWeek = getDay(date);
    return editedSchedule.working_days.includes(dayOfWeek);
  };

  const isTimeOffDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return editedSchedule.time_off?.includes(dateStr) || false;
  };

  const getDayClassName = (date: Date) => {
    const scheduled = isScheduledDay(date);
    const timeOff = isTimeOffDay(date);
    const inCurrentMonth = isSameMonth(date, currentMonth);
    
    if (!inCurrentMonth) {
      return 'text-muted-foreground/30';
    }
    
    if (timeOff) {
      return 'bg-destructive text-destructive-foreground hover:bg-destructive/90';
    }
    
    if (scheduled) {
      return 'bg-primary text-primary-foreground hover:bg-primary/90';
    }
    
    return 'text-muted-foreground hover:bg-accent';
  };

  const clearAllDates = () => {
    setEditedSchedule({ ...editedSchedule, time_off: [] });
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Pad the beginning to start on the correct day of week
    const firstDayOfWeek = getDay(monthStart);
    const paddingDays = Array(firstDayOfWeek).fill(null);
    
    const allDays = [...paddingDays, ...daysInMonth];
    
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-semibold">{format(currentMonth, 'MMMM yyyy')}</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
          
          {allDays.map((date, index) => {
            if (date === null) {
              return <div key={`empty-${index}`} />;
            }
            
            return (
              <Button
                key={date.toISOString()}
                variant="ghost"
                className={cn(
                  'h-12 w-full font-normal',
                  getDayClassName(date)
                )}
                onClick={() => handleDateClick(date)}
              >
                {format(date, 'd')}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const addRecurringBreak = () => {
    const recurring_breaks = editedSchedule.recurring_breaks || [];
    setEditedSchedule({
      ...editedSchedule,
      recurring_breaks: [
        ...recurring_breaks,
        { name: 'Break', start_time: '12:00', end_time: '13:00' }
      ]
    });
  };

  const removeRecurringBreak = (index: number) => {
    const recurring_breaks = (editedSchedule.recurring_breaks || []).filter((_, i) => i !== index);
    setEditedSchedule({ ...editedSchedule, recurring_breaks });
  };

  const updateRecurringBreak = (index: number, field: string, value: string) => {
    const recurring_breaks = [...(editedSchedule.recurring_breaks || [])];
    recurring_breaks[index] = { ...recurring_breaks[index], [field]: value };
    setEditedSchedule({ ...editedSchedule, recurring_breaks });
  };

  const addNonRecurringBreak = () => {
    const non_recurring_breaks = editedSchedule.non_recurring_breaks || [];
    setEditedSchedule({
      ...editedSchedule,
      non_recurring_breaks: [
        ...non_recurring_breaks,
        { name: 'Appointment', date: format(new Date(), 'yyyy-MM-dd'), start_time: '12:00', end_time: '13:00' }
      ]
    });
  };

  const removeNonRecurringBreak = (index: number) => {
    const non_recurring_breaks = (editedSchedule.non_recurring_breaks || []).filter((_, i) => i !== index);
    setEditedSchedule({ ...editedSchedule, non_recurring_breaks });
  };

  const updateNonRecurringBreak = (index: number, field: string, value: string) => {
    const non_recurring_breaks = [...(editedSchedule.non_recurring_breaks || [])];
    non_recurring_breaks[index] = { ...non_recurring_breaks[index], [field]: value };
    setEditedSchedule({ ...editedSchedule, non_recurring_breaks });
  };

  const handleSave = () => {
    onSave(editedSchedule);
    onOpenChange(false);
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="staff-schedule-editor-modal">
        <DialogHeader>
          <DialogTitle>
            {editedSchedule.display_name} - {editedSchedule.role.replace('_', ' ')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Working Days & Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Working Days & Hours
              </CardTitle>
              <CardDescription>
                Set which days this {editedSchedule.role === 'MANAGER' ? 'manager' : 'service writer'} works and their hours
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {daysOfWeek.map(({ label, value }) => (
                <div key={value} className="flex items-center gap-4">
                  <Checkbox
                    id={`day-${value}`}
                    checked={editedSchedule.working_days.includes(value)}
                    onCheckedChange={() => toggleWorkingDay(value)}
                    data-testid={`day-checkbox-${label.toLowerCase()}`}
                  />
                  <label htmlFor={`day-${value}`} className="flex-1 cursor-pointer font-medium min-w-[100px]">
                    {label}
                  </label>
                  {editedSchedule.working_days.includes(value) && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={editedSchedule.shift_start}
                        onChange={(e) => setEditedSchedule({ ...editedSchedule, shift_start: e.target.value })}
                        className="w-32"
                        data-testid="shift-start-input"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={editedSchedule.shift_end}
                        onChange={(e) => setEditedSchedule({ ...editedSchedule, shift_end: e.target.value })}
                        className="w-32"
                        data-testid="shift-end-input"
                      />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Mark Days Unavailable */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Mark Days Unavailable (Time Off)
                  </CardTitle>
                  <CardDescription>
                    Click dates when the {editedSchedule.role === 'MANAGER' ? 'manager' : 'service writer'} is NOT working. Weekly 'Working Days & Hours' sets the default; this calendar sets exceptions.
                  </CardDescription>
                </div>
                {editedSchedule.time_off && editedSchedule.time_off.length > 0 && (
                  <Button variant="outline" size="sm" onClick={clearAllDates}>
                    Clear All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/30 p-4 rounded-lg mb-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Important:</strong> Only click dates when the {editedSchedule.role === 'MANAGER' ? 'manager' : 'service writer'} is taking time off or unavailable. Their regular working schedule is set above in "Working Days & Hours".
                </p>
              </div>
              
              {renderCalendar()}
              
              <div className="mt-4 flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary"></div>
                  <span>Scheduled working days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-destructive"></div>
                  <span>Time off / Unavailable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-muted"></div>
                  <span>Not scheduled</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recurring Breaks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recurring Breaks
              </CardTitle>
              <CardDescription>Daily breaks that occur every working day</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(editedSchedule.recurring_breaks || []).map((breakItem, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Input
                    placeholder="Break name"
                    value={breakItem.name}
                    onChange={(e) => updateRecurringBreak(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="time"
                    value={breakItem.start_time}
                    onChange={(e) => updateRecurringBreak(index, 'start_time', e.target.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={breakItem.end_time}
                    onChange={(e) => updateRecurringBreak(index, 'end_time', e.target.value)}
                    className="w-32"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRecurringBreak(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addRecurringBreak} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Recurring Break
              </Button>
            </CardContent>
          </Card>

          {/* Non-Recurring Breaks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Non-Recurring Breaks
              </CardTitle>
              <CardDescription>One-time breaks for appointments, etc.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(editedSchedule.non_recurring_breaks || []).map((breakItem, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Input
                    placeholder="Break name"
                    value={breakItem.name}
                    onChange={(e) => updateNonRecurringBreak(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="date"
                    value={breakItem.date}
                    onChange={(e) => updateNonRecurringBreak(index, 'date', e.target.value)}
                    className="w-40"
                  />
                  <Input
                    type="time"
                    value={breakItem.start_time}
                    onChange={(e) => updateNonRecurringBreak(index, 'start_time', e.target.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={breakItem.end_time}
                    onChange={(e) => updateNonRecurringBreak(index, 'end_time', e.target.value)}
                    className="w-32"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNonRecurringBreak(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addNonRecurringBreak} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Non-Recurring Break
              </Button>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-schedule-button">
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-schedule-button">
              Save {editedSchedule.role === 'MANAGER' ? 'Manager' : 'Service Writer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
